import { Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { GROUP_COLOR_PALETTE, getGroupColor } from '@/lib/queues';
import { cn } from '@/lib/utils';

// Shared colour picker for swimlanes and sub-categories — the swatch + popover + 5-col palette
// grid lifted from the thread-groups editor. `usedColorIds` dims colours already taken by
// siblings (informational only; reuse is allowed).
export function ColorSwatchPopover({
  colorId,
  onChange,
  usedColorIds,
  disabled = false,
  label = 'Colour',
  size = 'md',
}) {
  const color = getGroupColor(colorId);
  const used = usedColorIds instanceof Set ? usedColorIds : new Set(usedColorIds || []);
  const triggerSize = size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`${label}: ${color.label}`}
          className={cn(
            'shrink-0 rounded-full border border-border/60 shadow-inner transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            triggerSize,
            disabled ? 'cursor-not-allowed opacity-60' : 'hover:scale-110',
            color.swatchClassName,
          )}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <div className="mb-1.5 text-[11px] font-semibold text-muted-foreground">{label}</div>
        <div className="grid grid-cols-5 gap-1.5">
          {GROUP_COLOR_PALETTE.map((entry) => {
            const isUsed = used.has(entry.id);
            const isActive = colorId === entry.id;
            return (
              <button
                key={entry.id}
                type="button"
                aria-label={entry.label}
                title={isUsed && !isActive ? `${entry.label} (used)` : entry.label}
                onClick={() => onChange(entry.id)}
                className={cn(
                  'relative h-7 w-7 rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  entry.swatchClassName,
                  isActive ? 'border-foreground ring-2 ring-ring' : 'border-border/60 hover:scale-110',
                  isUsed && !isActive ? 'opacity-50' : '',
                )}
              >
                {isActive ? (
                  <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" strokeWidth={3} />
                ) : null}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
